import React, { useCallback, useEffect, useRef, useState } from 'react'
import { AdvancedMarker, MapControl, ControlPosition, useMap, useMapsLibrary } from '@vis.gl/react-google-maps'

import { trans } from '../../_providers/Translation'

// The zoom the map is taken to when the layer is switched on, and pinned at
// while the viewport scan is running.
const GEOFENCE_ZOOM = 15

// The viewport is probed on a GRID x GRID lattice of sample points; every point
// that is not already covered by a known area gets reverse-geocoded.
const GRID = 3

// Safety valve so panning around for a long time cannot grow the layer without
// bound.
const MAX_FENCES = 80

// Nominatim asks for no more than one request a second.
const OSM_THROTTLE_MS = 1100

// Settle time after the map stops moving before the viewport is probed.
const SCAN_DEBOUNCE_MS = 350

// The search box looks itself up once typing has paused for this long.
const SEARCH_DEBOUNCE_MS = 500

// Short of this nothing is looked up, so partial postcodes cost no requests.
const MIN_SEARCH_LENGTH = 3

// Fallback half-size, in degrees, when no shape can be resolved at all.
const FALLBACK_SPAN = 0.009

const COLOR = '#e74c3c'

// The postcode labels read against the map rather than against the outline.
const LABEL_BG = '#262f5f'
const LABEL_FG = '#fff'

// google.maps.Polygon cannot draw a dashed stroke, so the outline is a separate
// Polyline made of repeated dash symbols over an invisible line.
const DASH_SYMBOL = {
    path: 'M 0,-1 0,1',
    strokeOpacity: 1,
    strokeColor: COLOR,
    strokeWeight: 1,
    scale: 2,
}

// The area matched by a search is drawn a little heavier than the rest.
const HIGHLIGHT_SYMBOL = {
    path: 'M 0,-1 0,1',
    strokeOpacity: 1,
    strokeColor: COLOR,
    strokeWeight: 1.5,
    scale: 3,
}

const FILL_OPACITY = 0.07
const HIGHLIGHT_FILL_OPACITY = 0.28

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms))

// ── Shape helpers ────────────────────────────────────────────────────────────

// GeoJSON rings are [lng, lat]; Google wants { lat, lng }.
function ringFromGeoJson(ring) {
    return ring
        .filter(p => Array.isArray(p) && p.length >= 2)
        .map(([lng, lat]) => ({ lat, lng }))
}

// Flatten a GeoJSON Polygon / MultiPolygon into a flat list of rings.
function ringsFromGeoJson(geojson) {
    if (!geojson || !geojson.coordinates) return []

    if (geojson.type === 'Polygon') {
        return geojson.coordinates.map(ringFromGeoJson).filter(r => r.length > 2)
    }

    if (geojson.type === 'MultiPolygon') {
        return geojson.coordinates
            .reduce((acc, polygon) => acc.concat(polygon.map(ringFromGeoJson)), [])
            .filter(r => r.length > 2)
    }

    return []
}

function ringFromBounds(bounds) {
    const ne = bounds.getNorthEast()
    const sw = bounds.getSouthWest()

    return [
        { lat: ne.lat(), lng: ne.lng() },
        { lat: ne.lat(), lng: sw.lng() },
        { lat: sw.lat(), lng: sw.lng() },
        { lat: sw.lat(), lng: ne.lng() },
    ]
}

function boxAround(lat, lng) {
    return [[
        { lat: lat + FALLBACK_SPAN, lng: lng + FALLBACK_SPAN },
        { lat: lat + FALLBACK_SPAN, lng: lng - FALLBACK_SPAN },
        { lat: lat - FALLBACK_SPAN, lng: lng - FALLBACK_SPAN },
        { lat: lat - FALLBACK_SPAN, lng: lng + FALLBACK_SPAN },
    ]]
}

function extentOfRings(rings) {
    const bounds = new window.google.maps.LatLngBounds()
    rings.forEach(ring => ring.forEach(p => bounds.extend(p)))

    const c = bounds.getCenter()
    const ne = bounds.getNorthEast()
    const sw = bounds.getSouthWest()

    return {
        center: { lat: c.lat(), lng: c.lng() },
        bounds: { north: ne.lat(), south: sw.lat(), east: ne.lng(), west: sw.lng() },
    }
}

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

// An area's label belongs on the part of it the user can actually see. A
// postcode wider than the viewport has its centre off screen, so the label is
// pulled back into the overlap between the area and the current view.
function labelPosition(fence, view) {
    if (!view) return fence.center

    const { center, bounds } = fence

    const visible = center.lat <= view.north && center.lat >= view.south
        && center.lng <= view.east && center.lng >= view.west

    if (visible) return center

    const south = Math.max(view.south, bounds.south)
    const north = Math.min(view.north, bounds.north)
    const west = Math.max(view.west, bounds.west)
    const east = Math.min(view.east, bounds.east)

    // No overlap — the area is off screen entirely.
    if (south > north || west > east) return center

    // Keep a little clear of the viewport edge.
    const latInset = (view.north - view.south) * 0.06
    const lngInset = (view.east - view.west) * 0.06

    return {
        lat: clamp(center.lat, south + latInset, north - latInset),
        lng: clamp(center.lng, west + lngInset, east - lngInset),
    }
}

// `types` is a priority order, so it has to be the outer loop: scanning results
// first returns whichever component Google happened to list earliest. Every
// London result carries locality=London, which then beats sublocality=Soho and
// labels every area in the city the same.
//
// The order matters for specificity too: at Mladost, neighborhood is
// "Mladost 1" while sublocality is the broader "Mladost".
function findComponent(results, types) {
    for (const type of types) {
        for (const result of results || []) {
            for (const component of result.address_components || []) {
                if (component.types.includes(type)) {
                    return component.long_name
                }
            }
        }
    }

    return null
}

// Postcodes are compared without spacing or case, because Google normalises
// what it returns: typing "ec4n4ya" comes back as "EC4N 4YA".
function normalizePostcode(value) {
    return (value || '').replace(/\s+/g, '').toUpperCase()
}

// The country a geocode result actually landed in, so a lookup outside the
// configured country still resolves its boundary against the right place.
function countryOf(results, fallback) {
    for (const result of results || []) {
        for (const component of result.address_components || []) {
            if (component.types.includes('country')) return component.short_name
        }
    }

    return fallback
}

// Sample points are deduped at roughly 100 m, so a small pan does not re-probe
// ground that has already been looked at.
function probeKey(lat, lng) {
    return lat.toFixed(3) + ',' + lng.toFixed(3)
}

function geocode(geocoder, request) {
    return new Promise(resolve => {
        geocoder.geocode(request, (results, status) => {
            const ok = status === window.google.maps.GeocoderStatus.OK
            resolve(ok && results && results.length ? results : null)
        })
    })
}

// Ask OSM for the real postcode boundary. Coverage is thin — as of writing only
// a small share of Bulgarian postcodes have a boundary=postal_code relation, the
// rest resolve to a bare centroid — so this always falls back to the Google
// bounding box. Nominatim's usage policy caps this at roughly one request per
// second and forbids bulk use, so point `osmEndpoint` at your own instance
// before turning it on for real traffic.
function fetchOsmRings(endpoint, code, country) {
    const url = endpoint
        + '?postalcode=' + encodeURIComponent(code)
        + '&country=' + encodeURIComponent(country)
        + '&format=json&polygon_geojson=1&limit=1'

    return fetch(url, { headers: { Accept: 'application/json' } })
        .then(res => (res.ok ? res.json() : null))
        .then(data => {
            if (!Array.isArray(data) || !data.length) return null
            return ringsFromGeoJson(data[0].geojson)
        })
        .catch(() => null)
}

// ─── Geofence layer — must be rendered inside a <Map> ─────────────────────────
//
// While `active` is on the map is zoomed in to `zoom` and every postcode area
// in the viewport is picked up automatically: each time the map settles the
// visible area is probed on a small grid and anything new is outlined in red
// dashed and labelled with its postcode.
//
// The zoom is locked for as long as that scan is running, so panning cannot
// pull half a country into range. Typing in the search box stops the scan and
// releases the lock, so a found area can be zoomed out of to see where it sits
// in its town; clearing the box zooms back in to `zoom`, starts the scan again
// and re-locks there.
//
// The outline follows the true postcode boundary when one can be resolved,
// otherwise the postcode's bounding box as reported by Google.
//
// Toggling the layer never pans the map — only the zoom changes, so whatever
// the user was looking at stays under the cursor.
function GeofenceLayer({
    active = false,
    country = 'BG',
    zoom: lockedZoom = GEOFENCE_ZOOM,
    useOsmBoundaries = false,
    osmEndpoint = 'https://nominatim.openstreetmap.org/search',
    onChange,
}) {
    const map = useMap()
    const geocodingLib = useMapsLibrary('geocoding')
    const geometryLib = useMapsLibrary('geometry')

    const [geocoder, setGeocoder] = useState(null)
    const [fences, setFences] = useState([])

    // Current viewport, used to keep every label on screen.
    const [view, setView] = useState(null)

    const [query, setQuery] = useState('')
    const [searchError, setSearchError] = useState(null)
    const [highlightId, setHighlightId] = useState(null)

    // Identifies the newest search, so a slow lookup that has been superseded
    // by more typing cannot land on top of a newer one.
    const searchSeqRef = useRef(0)

    // Anything in the box means the user is searching rather than browsing.
    const searchActive = query.trim().length > 0

    // The viewport scan runs only while the user is not searching, and the zoom
    // is locked only while the scan runs.
    const scanning = active && !searchActive

    // Read inside map listeners, which must not be re-registered on every fence
    // change (that would drop events while React re-renders).
    const fencesRef = useRef(fences)
    fencesRef.current = fences

    // Live google.maps.Polygon objects, keyed by fence id — kept so hit tests
    // can use containsLocation against the real geometry.
    const shapesRef = useRef(new Map())

    // Sample points already looked at, so the same ground is not re-geocoded.
    const probedRef = useRef(new Set())

    // Zoom settings to put back when the layer is switched off. The centre is
    // deliberately not captured — toggling must never pan the map.
    const restoreRef = useRef(null)

    useEffect(() => {
        if (geocodingLib) {
            setGeocoder(new geocodingLib.Geocoder())
        }
    }, [geocodingLib])

    useEffect(() => {
        if (typeof onChange === 'function') {
            onChange(fences)
        }
    }, [fences])

    // Zoom in on the way in, and put the old view back on the way out. Only the
    // zoom is touched: the centre is left exactly where the user put it, both
    // on the way in and on the way out. The lock that holds the zoom there is
    // the effect below.
    useEffect(() => {
        if (!map) return

        if (active) {
            if (!restoreRef.current) {
                restoreRef.current = {
                    zoom: map.getZoom(),
                    minZoom: map.get('minZoom') ?? null,
                    maxZoom: map.get('maxZoom') ?? null,
                }
            }

            // Clear the previous limits first, otherwise setZoom is clamped by
            // whatever range is currently in force.
            map.setOptions({ minZoom: null, maxZoom: null })
            map.setZoom(lockedZoom)

            return
        }

        const previous = restoreRef.current
        if (!previous) return
        restoreRef.current = null

        map.setOptions({
            minZoom: previous.minZoom,
            maxZoom: previous.maxZoom,
            scrollwheel: true,
            disableDoubleClickZoom: false,
        })

        if (previous.zoom !== undefined && previous.zoom !== null) {
            map.setZoom(previous.zoom)
        }
    }, [map, active, lockedZoom])

    // The zoom is locked only while the scan is running, because that is the
    // only thing the lock is there for: a scan at a wide zoom would probe half
    // the country. Searching stops the scan, so the zoom is handed back for as
    // long as there is something in the search box — that is what lets a found
    // area be zoomed out of and seen in the context of the town around it.
    //
    // Resuming the scan pulls the map back in to `lockedZoom` before re-locking,
    // whatever the user zoomed out to while searching. A scan left running at a
    // wide zoom would probe an enormous area and try to draw every region in it
    // at once, so the close zoom is a precondition of scanning, not a default.
    // Panning is untouched, so the map stays over the area that was searched
    // for — it is only pulled back in over it.
    useEffect(() => {
        if (!map || !active) return

        if (!scanning) {
            const previous = restoreRef.current

            map.setOptions({
                minZoom: previous ? previous.minZoom : null,
                maxZoom: previous ? previous.maxZoom : null,
                scrollwheel: true,
                disableDoubleClickZoom: false,
            })

            return
        }

        // Clear the limits first, otherwise setZoom is clamped by whatever
        // range is currently in force.
        map.setOptions({ minZoom: null, maxZoom: null })
        map.setZoom(lockedZoom)
        map.setOptions({
            minZoom: lockedZoom,
            maxZoom: lockedZoom,
            scrollwheel: false,
            disableDoubleClickZoom: true,
        })

        // minZoom/maxZoom only constrain user gestures — a programmatic setZoom
        // or fitBounds from anywhere else in the app sails straight past them.
        // This guard is what actually pins the zoom.
        const listener = map.addListener('zoom_changed', () => {
            if (map.getZoom() !== lockedZoom) {
                map.setZoom(lockedZoom)
            }
        })

        return () => window.google.maps.event.removeListener(listener)
    }, [map, active, scanning, lockedZoom])

    // Everything is dropped when the layer is switched off, so switching it
    // back on re-reads whatever is on screen at that moment.
    useEffect(() => {
        if (!active) {
            setFences([])
            setQuery('')
            setSearchError(null)
            setHighlightId(null)
            probedRef.current.clear()
        }
    }, [active])

    // Track the viewport so labels can be pulled back into view.
    useEffect(() => {
        if (!map || !active) {
            setView(null)
            return
        }

        const update = () => {
            const bounds = map.getBounds()
            if (!bounds) return

            const ne = bounds.getNorthEast()
            const sw = bounds.getSouthWest()

            setView({ north: ne.lat(), south: sw.lat(), east: ne.lng(), west: sw.lng() })
        }

        update()
        const listener = map.addListener('idle', update)

        return () => window.google.maps.event.removeListener(listener)
    }, [map, active])

    // Draw the outlines. Polygons and polylines are not React components in
    // @vis.gl/react-google-maps, so they are managed imperatively here.
    useEffect(() => {
        const shapes = shapesRef.current

        if (!map || !active) {
            shapes.forEach(({ polygon, outlines }) => {
                polygon.setMap(null)
                outlines.forEach(o => o.setMap(null))
            })
            shapes.clear()
            return
        }

        shapes.forEach((shape, id) => {
            if (fences.some(f => f.id === id)) return
            shape.polygon.setMap(null)
            shape.outlines.forEach(o => o.setMap(null))
            shapes.delete(id)
        })

        fences.forEach(fence => {
            if (shapes.has(fence.id)) return

            const polygon = new window.google.maps.Polygon({
                map,
                paths: fence.rings,
                strokeOpacity: 0,
                fillColor: COLOR,
                fillOpacity: FILL_OPACITY,
                // Clicks must reach the map, where the hit test resolves them.
                clickable: false,
                zIndex: 1,
            })

            const outlines = fence.rings.map(ring => new window.google.maps.Polyline({
                map,
                // Close the ring so the dashes wrap all the way round.
                path: ring.concat([ring[0]]),
                strokeOpacity: 0,
                icons: [{ icon: DASH_SYMBOL, offset: '0', repeat: '12px' }],
                clickable: false,
                zIndex: 2,
            }))

            shapes.set(fence.id, { polygon, outlines })
        })

        // Re-style so only the searched area is emphasised.
        shapes.forEach((shape, id) => {
            const on = id === highlightId

            shape.polygon.setOptions({
                fillOpacity: on ? HIGHLIGHT_FILL_OPACITY : FILL_OPACITY,
                zIndex: on ? 2 : 1,
            })

            shape.outlines.forEach(outline => outline.setOptions({
                icons: [{
                    icon: on ? HIGHLIGHT_SYMBOL : DASH_SYMBOL,
                    offset: '0',
                    repeat: on ? '13px' : '10px',
                }],
                zIndex: on ? 4 : 2,
            }))
        })
    }, [map, active, fences, highlightId])

    // Drop every overlay when the layer unmounts.
    useEffect(() => () => {
        shapesRef.current.forEach(({ polygon, outlines }) => {
            polygon.setMap(null)
            outlines.forEach(o => o.setMap(null))
        })
        shapesRef.current.clear()
    }, [])

    const addFence = useCallback((code, rings, neighborhood, source) => {
        if (!rings || !rings.length) return

        setFences(prev => {
            if (prev.length >= MAX_FENCES) return prev
            if (prev.some(f => f.postcode === code)) return prev

            return [...prev, {
                id: code,
                postcode: code,
                neighborhood,
                rings,
                source,
                ...extentOfRings(rings),
            }]
        })
    }, [])

    // The fence containing a point, if any.
    const fenceAt = useCallback((lat, lng) => {
        if (!geometryLib) return null

        const point = new window.google.maps.LatLng(lat, lng)

        for (const fence of fencesRef.current) {
            const shape = shapesRef.current.get(fence.id)
            if (!shape) continue

            if (geometryLib.poly.containsLocation(point, shape.polygon)) {
                return fence
            }
        }

        return null
    }, [geometryLib])

    // ── Automatic viewport scan ──────────────────────────────────────────────
    //
    // Paused while the search box has anything in it (`scanning`). Focusing a
    // searched area moves the map, which would otherwise fire a scan at the
    // destination and outline every neighbouring postcode alongside the one
    // searched for.
    useEffect(() => {
        if (!map || !scanning || !geocoder || !geometryLib) return

        let cancelled = false
        let timer = null

        // Resolve the outline for one postcode, preferring the real boundary.
        const resolveRings = async (code, results, lat, lng) => {
            const area = results.find(r => (r.types || []).includes('postal_code'))
            let bounds = area && (area.geometry.bounds || area.geometry.viewport)

            // The scan reverse-geocodes real points, so the country it landed
            // in is more reliable than the configured default.
            const where = countryOf(results, country)

            if (!bounds) {
                const areaResults = await geocode(geocoder, {
                    componentRestrictions: { country: where, postalCode: code },
                })

                const geometry = areaResults && areaResults[0] && areaResults[0].geometry
                bounds = geometry && (geometry.bounds || geometry.viewport)
            }

            const fallback = bounds ? [ringFromBounds(bounds)] : boxAround(lat, lng)

            if (!useOsmBoundaries) return { rings: fallback, source: 'bounds' }

            const osmRings = await fetchOsmRings(osmEndpoint, code, where)

            // Keep well inside Nominatim's rate limit.
            await sleep(OSM_THROTTLE_MS)

            return osmRings && osmRings.length
                ? { rings: osmRings, source: 'osm' }
                : { rings: fallback, source: 'bounds' }
        }

        const scan = async () => {
            const bounds = map.getBounds()
            if (!bounds) return

            const ne = bounds.getNorthEast()
            const sw = bounds.getSouthWest()

            const latSpan = ne.lat() - sw.lat()
            const lngSpan = ne.lng() - sw.lng()

            const points = []

            for (let row = 0; row < GRID; row++) {
                for (let col = 0; col < GRID; col++) {
                    points.push({
                        lat: sw.lat() + latSpan * ((row + 0.5) / GRID),
                        lng: sw.lng() + lngSpan * ((col + 0.5) / GRID),
                    })
                }
            }

            for (const point of points) {
                if (cancelled) return
                if (fencesRef.current.length >= MAX_FENCES) return

                const key = probeKey(point.lat, point.lng)

                // Already looked at, or already inside an area we know about.
                if (probedRef.current.has(key)) continue
                if (fenceAt(point.lat, point.lng)) continue

                probedRef.current.add(key)

                const results = await geocode(geocoder, {
                    location: { lat: point.lat, lng: point.lng },
                })
                if (cancelled) return
                if (!results) continue

                const code = findComponent(results, ['postal_code'])
                if (!code) continue
                if (fencesRef.current.some(f => f.postcode === code)) continue

                const neighborhood = findComponent(results, [
                    'neighborhood', 'sublocality', 'sublocality_level_1', 'locality', 'postal_town',
                ])

                const shape = await resolveRings(code, results, point.lat, point.lng)
                if (cancelled) return

                addFence(code, shape.rings, neighborhood, shape.source)
            }
        }

        const queueScan = () => {
            clearTimeout(timer)
            timer = setTimeout(scan, SCAN_DEBOUNCE_MS)
        }

        const listener = map.addListener('idle', queueScan)
        queueScan()

        return () => {
            cancelled = true
            clearTimeout(timer)
            window.google.maps.event.removeListener(listener)
        }
    }, [map, scanning, geocoder, geometryLib, country, useOsmBoundaries, osmEndpoint, addFence, fenceAt])

    // Bring an area on screen. The zoom is left alone — a searched area larger
    // than the viewport is centred rather than fitted, and its label is pulled
    // into view by labelPosition. The lock is off at this point, so the user is
    // free to zoom out from there themselves.
    const focusFence = useCallback(fence => {
        if (!map) return
        map.panTo(fence.center)
    }, [map])

    // Look up an exact postcode, draw it if it is not already on the map, then
    // highlight it and fit the view to it. `seq` identifies the search so a
    // superseded lookup can bail out instead of overwriting a newer one.
    const runSearch = useCallback(async (code, seq) => {
        if (!geocoder) return

        const stale = () => seq !== searchSeqRef.current

        const wanted = normalizePostcode(code)

        const known = fencesRef.current.find(f => normalizePostcode(f.postcode) === wanted)

        if (known) {
            setHighlightId(known.id)
            focusFence(known)
            return
        }

        // Restricting to the configured country first keeps a local code
        // resolving locally — "1784" is Sofia, not somewhere else that happens
        // to use the same number. Only if that finds nothing is the restriction
        // dropped, which is what lets a foreign postcode be searched at all.
        let results = await geocode(geocoder, {
            componentRestrictions: { country, postalCode: code },
        })
        if (stale()) return

        if (!results) {
            results = await geocode(geocoder, {
                componentRestrictions: { postalCode: code },
            })
            if (stale()) return
        }

        const geometry = results && results[0] && results[0].geometry
        const bounds = geometry && (geometry.bounds || geometry.viewport)

        // Only an exact match counts, so a near miss can never focus an area
        // the user did not ask for.
        const matched = results && findComponent(results, ['postal_code'])

        if (!bounds || normalizePostcode(matched) !== wanted) {
            setSearchError(trans('messages.postCodeNotFound'))
            return
        }

        const neighborhood = findComponent(results, [
            'neighborhood', 'sublocality', 'sublocality_level_1', 'locality', 'postal_town',
        ])

        // Label the area the way the postal service writes it, not the way it
        // happened to be typed.
        const postcode = matched

        let rings = [ringFromBounds(bounds)]
        let source = 'bounds'

        if (useOsmBoundaries) {
            const osmRings = await fetchOsmRings(osmEndpoint, postcode, countryOf(results, country))
            if (stale()) return

            if (osmRings && osmRings.length) {
                rings = osmRings
                source = 'osm'
            }
        }

        const fence = {
            id: postcode,
            postcode,
            neighborhood,
            rings,
            source,
            ...extentOfRings(rings),
        }

        setFences(prev => (prev.some(f => f.postcode === postcode) ? prev : [...prev, fence]))
        setHighlightId(postcode)
        focusFence(fence)
    }, [geocoder, country, useOsmBoundaries, osmEndpoint, focusFence])

    // Drop the highlight the moment what is typed stops matching the area that
    // is focused — deleting a character, or typing a different code, unfocuses
    // it immediately rather than waiting for the next lookup.
    useEffect(() => {
        const code = query.trim()

        setHighlightId(current => {
            if (!current) return current
            return current === code ? current : null
        })
    }, [query])

    // Search as the user types, once they pause.
    useEffect(() => {
        if (!active) return

        const code = query.trim()

        setSearchError(null)

        // Bumping the sequence here cancels anything already in flight.
        const seq = ++searchSeqRef.current

        if (code.length < MIN_SEARCH_LENGTH) return

        const timer = setTimeout(() => runSearch(code, seq), SEARCH_DEBOUNCE_MS)

        return () => clearTimeout(timer)
    }, [query, active, runSearch])

    if (!active) return null

    return (
        <>
            <MapControl position={ControlPosition.TOP_LEFT}>
                {/* .filter-standard so the field picks up the app's own input
                    styling rather than carrying its own. */}
                <div className="filter-standard" style={styles.searchBar}>
                    <div className="col">
                        <input
                            type="search"
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                            placeholder={trans('random.findPostCode')}
                            style={styles.searchInput}
                        />
                    </div>
                    {searchError && <span style={styles.searchError}>{searchError}</span>}
                </div>
            </MapControl>

            {/* Each area is labelled with its postcode and the name of the
                квартал, at the centre of the outline — pulled back into the
                viewport when that centre is off screen.

                No `clickable` prop: vis.gl reads any defined value — false
                included — as "clickable", which gives the marker a pointer
                cursor and forces pointer events on. */}
            {fences.map(f => (
                <AdvancedMarker key={f.id} position={labelPosition(f, view)} zIndex={3}>
                    <div style={styles.labelWrap}>
                        <div style={styles.label}>
                            <span style={styles.labelCode}>{f.postcode}</span>
                            {f.neighborhood && (
                                <span style={styles.labelArea}>{f.neighborhood}</span>
                            )}
                        </div>
                    </div>
                </AdvancedMarker>
            ))}

        </>
    )
}

const styles = {
    searchWrap: {
        margin: 10,
        width: 'fit-content',
    },
    searchBar: {
        // .filter-standard is a full-width page filter bar; as a map control it
        // has to hug its contents instead.
        display: 'inline-flex',
        width: 'fit-content',
        alignItems: 'center',
        gap: 8,
        margin: 0,
        // It also carries vertical padding meant for a page.
        padding: 6,
        // borderRadius: 6,
        // background: 'rgba(255, 255, 255, 0.95)',
        // boxShadow: '0 1px 4px rgba(0, 0, 0, 0.3)',
    },
    searchInput: {
        width: 150,
    },
    // Sits under the field, on its own panel so it stays readable over the map.
    // The colour is set here rather than inherited because `.App *` in app.scss
    // would otherwise win.
    searchError: {
        marginTop: 4,
        padding: '5px 8px',
        borderRadius: 4,
        background: '#fdecea',
        border: '1px solid #f3bcb6',
        color: '#a3312a',
        fontSize: 11,
        lineHeight: 1.3,
        maxWidth: 174,
        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.25)',
    },
    labelWrap: {
        // AdvancedMarker anchors content by its bottom centre; nudge it back so
        // the chip sits on the centre point.
        transform: 'translateY(50%)',
        pointerEvents: 'none',
    },
    label: {
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: 10,
        background: LABEL_BG,
        color: LABEL_FG,
        whiteSpace: 'nowrap',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)',
        lineHeight: 1.25,
    },
    // `.App *` in app.scss colours every descendant, and that rule matches
    // these spans directly — which beats the colour inherited from the chip.
    // Both therefore have to set it themselves.
    labelCode: {
        color: LABEL_FG,
        fontFamily: 'Inter Bold',
        fontWeight: 700,
        fontSize: 11,
    },
    labelArea: {
        color: LABEL_FG,
        fontSize: 10,
        opacity: 0.9,
    },
}

export default GeofenceLayer
